import React from 'react'
import { arrayMove } from 'ytil'
import { HomeScreenWidget, HomeScreenWidgetType } from '~/models'
import { observer } from '~/ui/component'
import { useForm } from '~/ui/form'
import { useBoolean } from '~/ui/hooks'
import ProjectSettingsFormModel from '../ProjectSettingsFormModel'

interface HomeScreenWidgetsContext {
  editingItems:      boolean
  startEditingItems: () => any
  stopEditingItems:  () => any

  configuringItemIndex:    number | null
  setConfiguringItemIndex: (index: number | null) => any

  formOpen:  boolean
  openForm:  () => any
  closeForm: () => any
  newWidgetType: HomeScreenWidgetType | null

  addWidget:    (type: HomeScreenWidget['type']) => any
  removeWidget: (index: number) => any
  moveWidget:   (originalIndex: number, newIndex: number) => any

  widgets:   HomeScreenWidget[]
}

interface HomeScreenWidgetsContextProviderProps {
  children?: React.ReactNode
}

const HomeScreenWidgetsContext = React.createContext<HomeScreenWidgetsContext>({
  editingItems:      false,
  startEditingItems: () => void 0,
  stopEditingItems:  () => void 0,

  configuringItemIndex:    null,
  setConfiguringItemIndex: () => void 0,

  formOpen:  false,
  openForm:  () => void 0,
  closeForm: () => void 0,
  newWidgetType: null,

  addWidget:    () => void 0,
  removeWidget: () => void 0,
  moveWidget:   () => void 0,

  widgets:      [],
})

const HomeScreenWidgetsContextProvider = observer('HomeScreenWidgetsContextProvider', (props: HomeScreenWidgetsContextProviderProps) => {

  const [editingItems, startEditingItems, stopEditingItems] = useBoolean(false)
  const [newWidgetType, setNewWidgetType] = React.useState<HomeScreenWidgetType | null>(null)
  const [configuringItemIndex, setConfiguringItemIndex] = React.useState<number | null>(null)
  const [formOpen, openForm, closeForm] = useBoolean(false)

  const {model} = useForm<ProjectSettingsFormModel>()

  const widgets = React.useMemo(
    () => model.homeScreenWidgets ?? [],
    [model.homeScreenWidgets],
  )

  const addWidget = React.useCallback((type: HomeScreenWidget['type']) => {
    const needsConfiguration = type !== 'agenda'
    if (!needsConfiguration) {
      model.homeScreenWidgets.push({type} as HomeScreenWidget)
      model.submit()
    } else {
      setConfiguringItemIndex(null)
      setNewWidgetType(type)
      openForm()
    }
  }, [model, openForm])

  const removeWidget = React.useCallback((index: number) => {
    model.homeScreenWidgets.splice(index, 1)
    model.submit()
  }, [model])

  const moveWidget = React.useCallback((originalIndex: number, newIndex: number) => {
    model.homeScreenWidgets = arrayMove(model.homeScreenWidgets, originalIndex, newIndex)
    model.submit()
  }, [model])

  const context = React.useMemo(() => ({
    editingItems,
    startEditingItems,
    stopEditingItems,

    formOpen,
    openForm,
    closeForm,
    newWidgetType,

    configuringItemIndex,
    setConfiguringItemIndex,

    addWidget,
    removeWidget,
    moveWidget,

    widgets,
  }), [addWidget, removeWidget, moveWidget, editingItems, startEditingItems, stopEditingItems, configuringItemIndex, setConfiguringItemIndex, widgets, formOpen, openForm, closeForm, newWidgetType])

  return(
    <HomeScreenWidgetsContext.Provider value={context}>
      {props.children}
    </HomeScreenWidgetsContext.Provider>
  )
})

export default HomeScreenWidgetsContextProvider

export function useHomeScreenWidgets() {
  return React.useContext(HomeScreenWidgetsContext)
}